import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import RightModalLayout from '../../layouts/Modal/RightModalLayout';
import { SIDEBAR_ACTIONS } from '../../../constants/actionsConstants';
import CallRecordingPlayerMC from '../../components/CallRecordingPlayerMC';
import IconMC from '../../components/IconMC';
import {
  displayedCallDuration,
  resolveCallDurationDisplay,
  unansweredRecordingMessageKey
} from '../../hooks/calls/callHistoryDisplayMapper';

/**
 * Sidebar that displays call details and allows playing the call recording.
 * Renders call metadata (name, contact, duration, date) and a recording player.
 *
 * @param {Object} props
 * @param {Call} props.call - Call data object with metadata and recording URL
 * @returns {React.ReactElement}
 */
const ViewCallSidebar = memo(({ call }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('CallEvent');

  const recordingUrl = call?.recording_url || call?.audio_url || '';
  const durationText = displayedCallDuration(call);
  const { labelKey: durationLabelKey } = resolveCallDurationDisplay(call);
  const recordingMessageKey = unansweredRecordingMessageKey(call);

  const closeSidebarAction = () => {
    dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
  };

  return (
    <div className="w-full">
      <RightModalLayout
        title={t('view_call_sidebar.title')}
        closeAction={closeSidebarAction}
      >
        <div className="tw-p-2 tw-flex tw-flex-col tw-gap-2">
          <div className="tw-flex tw-items-center tw-gap-1 tw-text-gray-500">
            <IconMC name="person-outline" size="sm" />
            <span className="tw-text-sm tw-font-normal">
              {t('view_call_sidebar.contact_label')}
            </span>
            <span className="tw-text-gray-950 tw-text-sm tw-font-normal">
              {call.customer?.full_name || call.customer?.phone}
            </span>
          </div>
          <div className="tw-flex tw-items-center tw-gap-1 tw-text-gray-500">
            <IconMC name="people-outline" size="sm" />
            <span className="tw-text-sm tw-font-normal">
              {t('view_call_sidebar.agent_label')}
            </span>
            <span className="tw-text-gray-950 tw-text-sm tw-font-normal">
              {call.agent?.full_name || t('view_call_sidebar.agent_unassigned')}
            </span>
          </div>
          {durationText && (
            <div className="tw-flex tw-items-center tw-gap-1 tw-text-gray-500">
              <IconMC name="clock-outline" size="sm" />
              <span className="tw-text-sm tw-font-normal">
                {t(durationLabelKey)}
              </span>
              <span className="tw-text-gray-950 tw-text-sm tw-font-normal">
                {durationText}
              </span>
            </div>
          )}
          <div className="tw-flex tw-items-center tw-gap-1 tw-text-gray-500">
            <IconMC name="calendar-outline" size="sm" />
            <span className="tw-text-sm tw-font-normal">
              {t('view_call_sidebar.date_label')}
            </span>
            <span className="tw-text-gray-950 tw-text-sm tw-font-normal">
              {new Date(call.started_at).toLocaleString()}
            </span>
          </div>
          <span className="tw-text-gray-950 tw-text-base tw-font-semibold tw-mt-4">
            {t('view_call_sidebar.recording_label')}
          </span>

          {recordingMessageKey ? (
            <p className="tw-text-gray-500 tw-text-sm">
              {t(recordingMessageKey)}
            </p>
          ) : (
            <CallRecordingPlayerMC recordingUrl={recordingUrl} />
          )}
        </div>
      </RightModalLayout>
    </div>
  );
});

ViewCallSidebar.displayName = 'ViewCallSidebar';

export default ViewCallSidebar;
